import { Button, Input } from "@heroui/react";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  InvitationResponse,
  NewRenterAcceptCallback,
  NewRenterInvitationData,
} from "./types";
import { InvitationInfoCard } from "./InvitationInfoCard";
import { InvitationFlowWrapper } from "./InvitationFlowWrapper";
import { StepHeader } from "./StepHeader";

interface NewRenterFlowProps {
  invitationData: InvitationResponse;
  onAccept: NewRenterAcceptCallback;
  isSubmitting?: boolean;
}

//

export function NewRenterFlow({
  invitationData,
  onAccept,
  isSubmitting = false,
}: NewRenterFlowProps) {
  const [activeStep, setActiveStep] = useState(0);
  const { agreement, meta } = invitationData;
  // opaqueId ранее использовался для localStorage; больше не нужен

  const defaultFormData: NewRenterInvitationData | null = meta
    ? {
        renter_name: meta.renter_name || "",
        address: meta.address || "",
        ogrn: meta.ogrn || 0,
        inn: meta.inn || 0,
        kpp: meta.kpp || 0,
        bik: meta.bik || "",
        bank_account: meta.bank_account || "",
        correspondent_account: meta.correspondent_account || "",
        bank_name: meta.bank_name || "",
        phone_number: meta.phone_number || "",
        email: meta.email || "",
      }
    : null;

  const form = useForm<NewRenterInvitationData>({
    defaultValues: defaultFormData || {
      renter_name: "",
      address: "",
      ogrn: 0,
      inn: 0,
      kpp: 0,
      bik: "",
      bank_account: "",
      correspondent_account: "",
      bank_name: "",
      phone_number: "",
      email: "",
    },
  });

  //

  const steps = [
    {
      name: "Информация о приглашении",
      description: "Ознакомьтесь с деталями приглашения",
      longDescription:
        "Вам поступило приглашение стать арендатором офиса. Пожалуйста, ознакомьтесь с информацией о приглашении.",
    },
    {
      name: "Данные арендатора",
      description: "Проверьте и отредактируйте данные арендатора",
      longDescription:
        "Проверьте данные арендатора. При необходимости отредактируйте их перед принятием приглашения.",
    },
    {
      name: "Проверка данных",
      description: "Проверьте все введенные данные",
      longDescription:
        "Проверьте все введенные данные перед принятием приглашения.",
    },
    {
      name: "Подтверждение",
      description: "Подтвердите принятие приглашения",
      longDescription:
        "Подтвердите принятие приглашения для завершения процесса.",
      hideTitle: true,
    },
  ];

  //

  const changeStep = useCallback(
    (step: number) => {
      // Разрешаем переход только на уже пройденные шаги (назад) или текущий
      if (step > activeStep) return;
      if (step === activeStep) return;
      setActiveStep(step);
    },
    [activeStep],
  );

  const handleFormSubmit = useCallback((_: NewRenterInvitationData) => {
    setActiveStep(2);
  }, []);

  const handleAccept = useCallback(() => {
    const formData = form.getValues();
    onAccept(formData);
  }, [form, onAccept]);

  // Определяем завершенные шаги для отображения в степпере
  const completedSteps = [];
  for (let i = 0; i < activeStep; i++) {
    completedSteps.push(i);
  }

  return (
    <InvitationFlowWrapper
      steps={steps}
      activeStep={activeStep}
      changeStep={changeStep}
      completedSteps={completedSteps}
      title="Приглашение в Elasticspace"
      subtitle={`${agreement.business_profile_name || "Бизнес-профиль"}: Создание нового арендатора`}>
      <div className="flex flex-col gap-4">
        <StepHeader step={steps[activeStep]} />

        {activeStep === 0 && (
          <div className="w-full flex flex-col gap-6">
            <InvitationInfoCard agreement={agreement} />
            <div className="flex justify-end">
              <Button
                className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                onClick={() => setActiveStep(1)}>
                Продолжить
              </Button>
            </div>
          </div>
        )}

        {activeStep === 1 && (
          <form
            onSubmit={form.handleSubmit(handleFormSubmit)}
            className="w-full flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                control={form.control}
                name="renter_name"
                rules={{ required: "Это поле обязательно" }}
                render={({ field }) => (
                  <Input
                    label="Название компании"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.renter_name}
                    errorMessage={form.formState.errors.renter_name?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="inn"
                rules={{
                  required: "Это поле обязательно",
                  pattern: {
                    value: /^\d+$/,
                    message: "ИНН должен состоять из цифр",
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="ИНН"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.inn}
                    errorMessage={form.formState.errors.inn?.message}
                    value={field.value?.toString() || ""}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
              <Controller
                control={form.control}
                name="address"
                rules={{ required: "Это поле обязательно" }}
                render={({ field }) => (
                  <Input
                    label="Адрес"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.address}
                    errorMessage={form.formState.errors.address?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="ogrn"
                rules={{
                  required: "Это поле обязательно",
                  pattern: {
                    value: /^\d+$/,
                    message: "ОГРН должен состоять из цифр",
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="ОГРН"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.ogrn}
                    errorMessage={form.formState.errors.ogrn?.message}
                    value={field.value?.toString() || ""}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
              <Controller
                control={form.control}
                name="kpp"
                rules={{
                  required: "Это поле обязательно",
                  pattern: {
                    value: /^\d+$/,
                    message: "КПП должен состоять из цифр",
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="КПП"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.kpp}
                    errorMessage={form.formState.errors.kpp?.message}
                    value={field.value?.toString() || ""}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
              <Controller
                control={form.control}
                name="phone_number"
                rules={{
                  required: "Это поле обязательно",
                }}
                render={({ field }) => (
                  <Input
                    label="Телефон"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.phone_number}
                    errorMessage={form.formState.errors.phone_number?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="email"
                rules={{
                  required: "Это поле обязательно",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Введите корректный email",
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="Email"
                    type="email"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.email}
                    errorMessage={form.formState.errors.email?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="bik"
                rules={{
                  required: "Это поле обязательно",
                }}
                render={({ field }) => (
                  <Input
                    label="БИК"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.bik}
                    errorMessage={form.formState.errors.bik?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="bank_account"
                rules={{
                  required: "Это поле обязательно",
                }}
                render={({ field }) => (
                  <Input
                    label="Номер счета"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.bank_account}
                    errorMessage={form.formState.errors.bank_account?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="correspondent_account"
                rules={{
                  required: "Это поле обязательно",
                }}
                render={({ field }) => (
                  <Input
                    label="Корреспондентский счет"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.correspondent_account}
                    errorMessage={
                      form.formState.errors.correspondent_account?.message
                    }
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="bank_name"
                rules={{
                  required: "Это поле обязательно",
                }}
                render={({ field }) => (
                  <Input
                    label="Название банка"
                    variant="bordered"
                    isRequired
                    isInvalid={!!form.formState.errors.bank_name}
                    errorMessage={form.formState.errors.bank_name?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium">
                Продолжить
              </Button>
            </div>
          </form>
        )}

        {activeStep === 2 && (
          <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col gap-6">
              <InvitationInfoCard agreement={agreement} />
              <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Данные арендатора
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">
                      Название компании
                    </span>
                    <p className="font-medium">
                      {form.watch("renter_name") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">ИНН</span>
                    <p className="font-medium">
                      {form.watch("inn") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">ОГРН</span>
                    <p className="font-medium">
                      {form.watch("ogrn") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">КПП</span>
                    <p className="font-medium">
                      {form.watch("kpp") || "Не указано"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-sm text-gray-500">Адрес</span>
                    <p className="font-medium">
                      {form.watch("address") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Телефон</span>
                    <p className="font-medium">
                      {form.watch("phone_number") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <p className="font-medium">
                      {form.watch("email") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">БИК</span>
                    <p className="font-medium">
                      {form.watch("bik") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Номер счета</span>
                    <p className="font-medium">
                      {form.watch("bank_account") || "Не указано"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">
                      Корреспондентский счет
                    </span>
                    <p className="font-medium">
                      {form.watch("correspondent_account") || "Не указано"}
                    </p>
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-sm text-gray-500">
                      Название банка
                    </span>
                    <p className="font-medium">
                      {form.watch("bank_name") || "Не указано"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Button variant="light" onPress={() => setActiveStep(1)}>
                Назад
              </Button>
              <Button
                className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                onClick={() => setActiveStep(3)}>
                Продолжить
              </Button>
            </div>
          </div>
        )}

        {activeStep === 3 && (
          <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-bold text-foreground">
                Подтверждение
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Вы подтверждаете принятие приглашения стать арендатором офиса "
                {agreement.office_name}" от бизнес-профиля "
                {agreement.business_profile_name}".
              </p>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Все данные корректны и готовы к отправке.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <Button variant="light" onPress={() => setActiveStep(2)}>
                Назад
              </Button>
              <Button
                className="bg-elastic-500 hover:bg-elastic-600 text-black font-medium"
                onClick={handleAccept}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}>
                Принять приглашение
              </Button>
            </div>
          </div>
        )}
      </div>
    </InvitationFlowWrapper>
  );
}
